import { useEffect } from "react";
import { SavedSearch } from "../../../../types/neighborhoodSearch";
import { SavedSearchCard } from "./SavedSearchCard";
import { Input } from "src/components/core/Input";
import useScrollToManager from "src/hooks/useScrollToManager";
import { useScrollBottomCheck } from "src/hooks/useScrollBottomCheck";
import { Loading } from "./Loading";

export interface ListSearchesSidebarProps {
  searches: SavedSearch[];
  expandedSearchId?: string;
  mapExpandedSavedSearchId?: string;
  focusedSearchId?: string | null;
  onSearchClick: (search: SavedSearch) => void;
  onSearchSelectionChange: (search: SavedSearch, isSelected: boolean) => void;
  selectedSearches: SavedSearch[];
  onScrollBottomReached?: () => void;
  onSearchTermChange?: (searchTerm: string) => void;
  searchTerm?: string;
  isLoading?: boolean;
  onSearchToggleClick?: (search: SavedSearch) => void;
}

export const ListSearchesSidebar = ({
  searches,
  expandedSearchId,
  mapExpandedSavedSearchId,
  focusedSearchId,
  onSearchClick,
  onSearchSelectionChange,
  selectedSearches,
  onScrollBottomReached,
  onSearchTermChange,
  searchTerm,
  isLoading = false,
  onSearchToggleClick,
}: ListSearchesSidebarProps) => {
  const { addScrollToItem, scrollToItemWithId } = useScrollToManager();
  const containerRef = useScrollBottomCheck(onScrollBottomReached);

  useEffect(() => {
    if (mapExpandedSavedSearchId) {
      scrollToItemWithId(mapExpandedSavedSearchId);
    }
  }, [mapExpandedSavedSearchId, scrollToItemWithId]);

  return (
    <div className="flex flex-col h-full">
      {onSearchTermChange && (
        <div className="pb-2">
          <Input
            value={searchTerm}
            onChange={(e) => onSearchTermChange(e.target.value)}
            placeholder="Search saved searches..."
          />
        </div>
      )}
      <div ref={containerRef} className="flex flex-col flex-1 overflow-y-auto gap-2">
        {searches.map((search) => (
          <SavedSearchCard
            key={search.id}
            search={search}
            isExpanded={search.id === expandedSearchId}
            isSelected={selectedSearches.some((s) => s.id === search.id)}
            isFocused={search.id === focusedSearchId}
            onClick={() => onSearchClick(search)}
            onSelectedChange={(isSelected: boolean) =>
              onSearchSelectionChange(search, isSelected)
            }
            onExpandToggleClick={() => onSearchToggleClick?.(search)}
            ref={(element) => {
              if (element) {
                addScrollToItem(element, search.id, 0);
              }
            }}
          />
        ))}
        {isLoading && <Loading />}
        {!isLoading && !searches.length && (
          <div className="text-center text-gray-500 text-base mt-4">
            No matching searches found.
          </div>
        )}
      </div>
    </div>
  );
};
